<script setup>
import { ref, computed } from 'vue';
import { useForm, router } from '@inertiajs/vue3';

import VueMultiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.css";

const props = defineProps({
    template: { type: Object, required: true },
    activeFieldId: { type: [Number, String], default: null },
    currentPage: { type: Number, default: 1 },
    urlFieldStore: { type: String, required: true },
    urlFieldUpdate: { type: String, required: true },
    urlFieldDelete: { type: String, required: true },
    urlFieldReorder: { type: String, required: true },
    fieldSuggestions: { type: Array, default: () => [] },
});

const emit = defineEmits(['select-field']);

const availableFieldSuggestions = computed(() => {
    const existingNames = props.template.document_field_config?.map(f => f.name.toLowerCase()) || [];
    return props.fieldSuggestions
        .filter(s => !existingNames.includes(s.name.toLowerCase()))
        .map(s => ({
            ...s,
            displayLabel: `${s.name} (${s.code})`
        }));
});

const selectedSuggestion = ref(null);

const addCustomName = (newTag) => {
    const code = newTag.toLowerCase().replace(/[^a-z0-9]/g, '_');
    selectedSuggestion.value = { 
        name: newTag, 
        code: code, 
        type: 'text',
        displayLabel: `${newTag} (${code})`
    };
    form.name = selectedSuggestion.value.name;
    form.code = selectedSuggestion.value.code;
};

const onSuggestionSelected = (option) => {
    form.name = option.name;
    form.code = option.code;
    form.type = option.type;
};

const isAdding = ref(false);

const form = useForm({
    name: '',
    code: '',
    type: 'text',
    rules: [],
    config: {
        locations: [],
        format: ''
    }
});

const fieldTypes = [
    { value: 'text', label: 'Text' },
    { value: 'textarea', label: 'Textarea' },
    { value: 'sign', label: 'Signature Image' },
    { value: 'upload', label: 'Upload Image' },
    { value: 'accept', label: 'Accept Checkbox' },
    { value: 'multiselect', label: 'Multi-select' },
    { value: 'date', label: 'Date' },
];

const getFieldUpdateUrl = (fieldId) => {
    return props.urlFieldUpdate.replace('__FIELD_ID__', fieldId);
};

const getFieldDeleteUrl = (fieldId) => {
    return props.urlFieldDelete.replace('__FIELD_ID__', fieldId);
};

const startAddField = () => {
    isAdding.value = true;
    form.reset();
    form.rules = [];
    form.config.locations = [];
    form.config.format = '';
    selectedSuggestion.value = null;
    emit('select-field', 'new');
};

const cancelAddField = () => {
    isAdding.value = false;
    form.reset();
    selectedSuggestion.value = null;
    emit('select-field', null);
};

const saveNewField = () => {
    form.post(props.urlFieldStore, {
        preserveScroll: true,
        onSuccess: () => {
            isAdding.value = false;
            form.reset();
            selectedSuggestion.value = null;
            emit('select-field', null);
        }
    });
};

const deleteField = (fieldId) => {
    if (confirm('Are you sure you want to delete this field?')) {
        router.delete(getFieldDeleteUrl(fieldId), {
            preserveScroll: true,
            onSuccess: () => {
                if (props.activeFieldId === fieldId) {
                    emit('select-field', null);
                }
            }
        });
    }
};

const moveField = (index, direction) => {
    if (!props.template.document_field_config) return;
    
    const fields = [...props.template.document_field_config];
    if (direction === 'up' && index > 0) {
        [fields[index - 1], fields[index]] = [fields[index], fields[index - 1]];
    } else if (direction === 'down' && index < fields.length - 1) {
        [fields[index + 1], fields[index]] = [fields[index], fields[index + 1]];
    } else {
        return;
    }
    
    // Update local state temporarily for fast UI
    props.template.document_field_config = fields;
    
    const orderedIds = fields.map(f => f.id);
    router.put(props.urlFieldReorder, { ordered_ids: orderedIds }, { preserveScroll: true });
};

const removeLocation = (field, locIndex) => {
    const locations = [...field.config.locations];
    locations.splice(locIndex, 1);
    
    useForm({
        name: field.name,
        code: field.code,
        type: field.type,
        rules: field.rules || [],
        options: field.options || [],
        config: { ...field.config, locations: locations }
    }).put(getFieldUpdateUrl(field.id), {
        preserveScroll: true
    });
};

const updateLocationSize = (field, locIndex, size) => {
    const locations = [...field.config.locations];
    locations[locIndex].font_size = parseInt(size);
    
    useForm({
        name: field.name,
        code: field.code,
        type: field.type,
        rules: field.rules || [],
        options: field.options || [],
        config: { ...field.config, locations: locations }
    }).put(getFieldUpdateUrl(field.id), {
        preserveScroll: true
    });
};

const updateLocationCoord = (field, locIndex, axis, value) => {
    const locations = [...field.config.locations];
    locations[locIndex][axis] = parseFloat(value);
    
    useForm({
        name: field.name,
        code: field.code,
        type: field.type,
        rules: field.rules || [],
        options: field.options || [],
        config: { ...field.config, locations: locations }
    }).put(getFieldUpdateUrl(field.id), {
        preserveScroll: true
    });
};

const toggleRequired = (field) => {
    let rules = field.rules ? [...field.rules] : [];
    if (rules.includes('required')) {
        rules = rules.filter(r => r !== 'required');
    } else {
        rules.push('required');
    }
    
    useForm({
        name: field.name,
        code: field.code,
        type: field.type,
        rules: rules,
        options: field.options || [],
        config: field.config || {}
    }).put(getFieldUpdateUrl(field.id), {
        preserveScroll: true
    });
};

const updateFieldCode = (field, newCode) => {
    if (!newCode || newCode === field.code) return;
    useForm({
        name: field.name,
        code: newCode,
        type: field.type,
        rules: field.rules || [],
        options: field.options || [],
        config: field.config || {}
    }).put(getFieldUpdateUrl(field.id), {
        preserveScroll: true
    });
};

const selectField = (fieldId) => {
    emit('select-field', fieldId);
};
</script>

<template>
    <div class="field-config-panel">
        
        <!-- Add New Field Form -->
        <div v-if="isAdding" class="card border-brand shadow-sm mb-4">
            <div class="card-header bg-brand-light border-0 py-3">
                <h6 class="mb-0 fw-bold text-brand"><i class="fas fa-plus-circle me-2"></i>Add New Field</h6>
            </div>
            <div class="card-body">
                <div class="mb-3">
                    <label class="form-label small fw-bold">Field Name</label>
                    <VueMultiselect
                        v-model="selectedSuggestion"
                        :options="availableFieldSuggestions"
                        track-by="name"
                        label="displayLabel"
                        :taggable="true"
                        @tag="addCustomName"
                        @select="onSuggestionSelected"
                        placeholder="Type to add or select..."
                        select-label=""
                        selected-label=""
                        deselect-label=""
                    />
                </div>
                <div class="mb-3">
                    <label class="form-label small fw-bold">Code (DB Column/Key)</label>
                    <input v-model="form.code" type="text" class="form-control form-control-sm" placeholder="e.g. name" required>
                </div>
                <div class="mb-3">
                    <label class="form-label small fw-bold">Type</label>
                    <select v-model="form.type" class="form-select form-select-sm" required>
                        <option v-for="t in fieldTypes" :key="t.value" :value="t.value">{{ t.label }}</option>
                    </select>
                </div>

                <div v-if="form.type === 'date'" class="mb-3">
                    <label class="form-label small fw-bold">Date Format</label>
                    <input v-model="form.config.format" type="text" class="form-control form-control-sm" placeholder="e.g. d / m / Y">
                    <div class="form-text" style="font-size: 0.7rem;">Leave empty for default</div>
                </div>

                <div class="mb-3">
                    <div class="form-check">
                        <input class="form-check-input" type="checkbox" id="fieldRequired" v-model="form.rules" value="required">
                        <label class="form-check-label small fw-bold" for="fieldRequired">
                            Required Field
                        </label>
                    </div>
                </div>

                <div class="alert alert-info py-2 px-3 small mb-3">
                    <i class="fas fa-info-circle me-1"></i> Save this field first, then click on the PDF preview to add its locations.
                </div>

                <div class="d-flex justify-content-end gap-2">
                    <button @click="cancelAddField" class="btn btn-sm btn-light border" type="button">Cancel</button>
                    <button @click="saveNewField" class="btn btn-sm btn-premium" type="button" :disabled="form.processing">
                        <span v-if="form.processing" class="spinner-border spinner-border-sm"></span>
                        Save Field
                    </button>
                </div>
            </div>
        </div>

        <!-- Add Field Button -->
        <button v-else @click="startAddField" class="btn btn-outline-brand w-100 mb-4 dashed-border">
            <i class="fas fa-plus me-2"></i> Add New Field
        </button>


        <!-- List of Existing Fields -->
        <h6 class="fw-bold mb-3 text-secondary text-uppercase" style="font-size: 0.8rem; letter-spacing: 0.5px;">Configured Fields ({{ template.document_field_config?.length || 0 }})</h6>
        
        <div class="field-list">
            <div v-if="!template.document_field_config?.length" class="text-center text-muted p-4 small">
                No fields added yet.
            </div>

            <div class="field-list-grid">
                <div v-for="(field, index) in template.document_field_config" :key="field.id" 
                    class="p-2 border rounded bg-white field-item"
                    :class="{ 'border-brand shadow-sm': activeFieldId === field.id }"
                    @click="selectField(field.id)">
                    
                    <div class="d-flex justify-content-between align-items-start mb-2">
                        <div class="flex-grow-1 min-w-0">
                            <div class="mb-1 text-truncate" :title="field.name">
                                <span class="text-muted fw-normal me-1" style="font-size: 0.65rem;">Name:</span>
                                <span class="fw-bold text-dark" style="font-size: 0.9rem;">{{ field.name }}</span>
                            </div>
                            <div class="small text-muted d-flex align-items-center gap-1 flex-wrap w-100">
                                <span class="text-muted fw-normal" style="font-size: 0.65rem;">Code:</span>
                                <input 
                                    type="text" 
                                    :value="field.code" 
                                    @change="e => updateFieldCode(field, e.target.value)" 
                                    @click.stop
                                    class="form-control form-control-sm px-1 py-0 d-inline-block bg-light border text-dark font-monospace" 
                                    style="width: 80px; height: 20px; font-size: 0.65rem;"
                                    title="Edit Code"
                                >
                                <span class="text-muted fw-normal ms-1" style="font-size: 0.65rem;">Type:</span>
                                <span class="badge bg-secondary" style="font-size: 0.6rem;">{{ field.type }}</span>
                                <div class="form-check form-switch mb-0 d-flex align-items-center ms-auto" title="Toggle Required">
                                    <input 
                                        class="form-check-input mt-0" 
                                        type="checkbox" 
                                        :id="'reqToggle_' + field.id"
                                        :checked="field.rules?.includes('required')"
                                        @change="toggleRequired(field)"
                                        @click.stop
                                        style="transform: scale(0.75); cursor: pointer;"
                                    >
                                    <label class="form-check-label ms-1 mb-0" :for="'reqToggle_' + field.id" @click.stop style="font-size: 0.7rem; cursor: pointer; user-select: none;">Required</label>
                                </div>
                            </div>
                            <div v-if="field.type === 'date' && field.config?.format" class="small fst-italic text-muted mt-1" style="font-size: 0.65rem;">
                                Fmt: {{ field.config.format }}
                            </div>
                        </div>
                        <div class="d-flex flex-column gap-1 ms-1 flex-shrink-0 border-start ps-2">
                            <div class="d-flex gap-1">
                                <button @click.stop="moveField(index, 'up')" :disabled="index === 0" class="btn btn-sm btn-light p-0 border text-secondary" style="width: 20px; height: 20px; display: flex; align-items: center; justify-content: center;" title="Move Up">
                                    <i class="fas fa-chevron-up" style="font-size: 0.6rem;"></i>
                                </button>
                                <button @click.stop="moveField(index, 'down')" :disabled="index === (template.document_field_config?.length || 0) - 1" class="btn btn-sm btn-light p-0 border text-secondary" style="width: 20px; height: 20px; display: flex; align-items: center; justify-content: center;" title="Move Down">
                                    <i class="fas fa-chevron-down" style="font-size: 0.6rem;"></i>
                                </button>
                            </div>
                            <button @click.stop="deleteField(field.id)" class="btn btn-sm btn-link text-danger p-0 mt-auto text-end" title="Delete Field" style="text-decoration: none; font-size: 0.8rem;">
                                <i class="fas fa-trash-alt"></i>
                            </button>
                        </div>
                    </div>

                    <!-- Locations list -->
                    <div class="locations-wrapper mt-2 pt-2 border-top">
                        <div class="small fw-bold text-secondary mb-2" style="font-size: 0.7rem;">Locations ({{ field.config?.locations?.length || 0 }})</div>
                        
                        <div v-if="!field.config?.locations?.length" class="small text-muted fst-italic" style="font-size: 0.65rem;">
                            Click on the PDF preview to add a location.
                        </div>

                        <div 
                            v-for="(loc, idx) in field.config?.locations || []" 
                            :key="idx"
                            class="location-item small d-flex align-items-center bg-light p-1 rounded mb-1 border gap-1 flex-nowrap overflow-hidden"
                            :class="{'border-brand bg-brand-light': loc.page === currentPage}"
                        >
                            <span class="badge bg-secondary flex-shrink-0" style="font-size: 0.6rem;">P{{ loc.page }}</span>
                            
                            <div class="d-flex align-items-center flex-shrink-0">
                                <span class="text-muted me-1" style="font-size: 0.65rem;">X:</span>
                                <input type="number" step="0.1" :value="loc.x" @change="e => updateLocationCoord(field, idx, 'x', e.target.value)" class="form-control form-control-sm px-1 py-0 no-arrow" style="width: 46px; height: 24px; font-size: 0.7rem;">
                            </div>
                            
                            <div class="d-flex align-items-center flex-shrink-0">
                                <span class="text-muted me-1" style="font-size: 0.65rem;">Y:</span>
                                <input type="number" step="0.1" :value="loc.y" @change="e => updateLocationCoord(field, idx, 'y', e.target.value)" class="form-control form-control-sm px-1 py-0 no-arrow" style="width: 46px; height: 24px; font-size: 0.7rem;">
                            </div>
                            
                            <div v-if="field.type === 'text'" class="d-flex align-items-center flex-shrink-0">
                                <span class="text-muted me-1" style="font-size: 0.65rem;">Sz:</span>
                                <input type="number" :value="loc.font_size || 9" @change="e => updateLocationSize(field, idx, e.target.value)" class="form-control form-control-sm px-1 py-0 no-arrow" style="width: 42px; height: 24px; font-size: 0.7rem;">
                            </div>
                            
                            <button @click.stop="removeLocation(field, idx)" class="btn btn-sm btn-link text-danger p-0 ms-auto flex-shrink-0">
                                <i class="fas fa-times" style="font-size: 0.7rem;"></i>
                            </button>
                        </div>
                        
                        <div v-if="activeFieldId === field.id" class="alert alert-warning py-1 px-2 mt-2 mb-0 small border-0 bg-brand-light text-brand">
                            <i class="fas fa-crosshairs me-1"></i> Click on PDF to add location here
                        </div>
                    </div>
                </div>
            </div>
        </div>

    </div>
</template>

<style scoped>
.field-config-panel {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
}
.field-list {
    flex: 1;
    overflow-y: auto;
    padding-right: 5px;
    min-height: 0;
}
.field-list::-webkit-scrollbar { width: 6px; }
.field-list::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
.field-list::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
.field-list::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
.field-list-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    align-content: start;
}

.text-brand { color: #8a2529 !important; }
.border-brand { border-color: #8a2529 !important; }
.bg-brand-light { background-color: rgba(138, 37, 41, 0.05) !important; }

.field-card {
    cursor: pointer;
    transition: all 0.2s ease;
    border-radius: 8px;
}
.field-card:hover {
    box-shadow: 0 4px 8px rgba(0,0,0,0.05);
    transform: translateY(-1px);
}
.active-field-card {
    box-shadow: 0 0 0 2px rgba(138, 37, 41, 0.2);
    background-color: #fcf9f9;
}

.dashed-border {
    border-style: dashed !important;
    border-width: 2px !important;
}

.btn-premium {
    background: linear-gradient(135deg, #8a2529 0%, #aa3a3e 100%);
    color: #fff;
    border: none;
}
.btn-premium:hover { background: linear-gradient(135deg, #741d20 0%, #8a2529 100%); color: #fff;}
.btn-outline-brand { color: #8a2529; border-color: #8a2529; }
.btn-outline-brand:hover { background-color: rgba(138, 37, 41, 0.05); color: #8a2529; }

/* Remove number input arrows */
input[type=number].no-arrow::-webkit-inner-spin-button, 
input[type=number].no-arrow::-webkit-outer-spin-button { 
    -webkit-appearance: none; 
    margin: 0; 
}
input[type=number].no-arrow {
    -moz-appearance: textfield;
}
</style>
